import React from 'react';

import { Button, Divider } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import { logger } from '@tecma/logs-fe';
import { useDispatch, useSelector } from 'react-redux';

import { deleteRequest, downloadZip, getClientDocuments } from 'store/sagas/configurations/configurations.api';
import { applicationStateSelectors } from 'store/slices/applicationState/selectors';
import { applicationStateActions } from 'store/slices/applicationState/slice';
import { configurationsSelectors } from 'store/slices/configurations/selectors';
import { setConfigurations } from 'store/slices/configurations/slice';
import { environmentSelectors } from 'store/slices/environment/selector';
import { getUserClientId } from 'store/slices/auth/selectors';

import { ApartmentStatusTag } from 'components/ApartmentStatusTag';
import type { QuoteSpecProps } from 'components/QuoteSpec/QuoteSpec';
import { QuoteSpecSection } from 'components/QuoteSpecSection';
import { QuoteDetailsActions } from 'components/QuoteDetailsActions/QuoteDetailsActions';

import { displayPrice } from 'utils/displayPrice';
import { getStatusColor } from 'utils/status';
import { formatDate } from 'utils/formatDates';

import type { Quote } from 'types/quote';
import type { Document } from 'types/document';

import './quote-details-container.scss';

const PROMO_INTERIOR_PACKS = [
  {
    label: 'Special promo (1,38%)',
    value: -4500,
    frequency: 'single',
  },
  {
    label: 'Interior pack',
    value: true,
  },
];

const PAYMENT_DETAILS = [
  {
    label: 'Monthly rate',
    subtitle: 'Condominium expenses and VAT included (10%).',
    value: 150,
  },
  {
    label: 'Security deposit',
    subtitle: '3 months rental payment(VAT excluded) and condominium expenses.',
    value: 4500,
    frequency: 'single',
  },
  {
    label: 'Spese condominiali',
    subtitle: 'Salvo conguaglio annuale',
    value: true,
  },
];

const ADDITIONAL_EXPENSES = [
  {
    label: 'Agency fees (1,38%)',
    value: 4500,
    frequency: 'single',
  },
  {
    label: 'Connection fees (1,80%)',
    value: 5200,
    frequency: 'single',
  },
  {
    label: 'Notary fees',
    value: 'To be defined',
  },
];

interface QuoteDetailsContainerRequiredProps {
  quote: Quote;
}

// Optional Props
interface QuoteDetailsContainerOptionalProps {
  footerActionOnClose?: () => void;
  footerActionOnSubmit?: () => void;
  onQuoteDeleted?: () => void;
}

// Combined required and optional props to build the full prop interface
export interface QuoteDetailsContainerProps extends QuoteDetailsContainerRequiredProps, QuoteDetailsContainerOptionalProps {}

const QuoteDetailsContainer: React.FC<QuoteDetailsContainerProps> = ({
  quote,
  footerActionOnClose,
  footerActionOnSubmit,
  onQuoteDeleted,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const area = useSelector(applicationStateSelectors.selectArea);
  const assetTypology = useSelector(applicationStateSelectors.selectAssetTypology);
  const projectId = useSelector(applicationStateSelectors.selectProjectId);
  const configurations = useSelector(configurationsSelectors.selectConfigurations);
  const displayName = useSelector(applicationStateSelectors.selectDisplayName);
  const environment = useSelector(environmentSelectors.environment);
  const clientId = useSelector(getUserClientId);
  const getExtraSpaces = quote.features;
  const documentsBucketUrl = `${environment.REACT_APP_BUCKET_BASEURL}/initiatives/${encodeURI(displayName || '')}/global/documents`;

  const getMoodAndOptionals = () => {
    const optionals: QuoteSpecProps[] =
      quote.packs?.map(({ price, name }) => ({
        value: Math.round(price),
        label: t(`pack.${name}`, { defaultValue: name.charAt(0).toUpperCase() + name.slice(1) }) + ' ' + t('pack.label'),
      })) ?? [];
    if (quote.mood) {
      optionals.unshift({ label: 'Mood', value: quote.mood.name });
    }
    return optionals;
  };

  const handleOpenInformativeNotes = () => {
    window.open(`${documentsBucketUrl}/informative-notes.pdf`, '_blank');
  };

  const handleOpenPdf = () => {
    if (quote.pdfPath) window.open(quote.pdfPath, '_blank');
  };

  const handleDeleteQuote = async () => {
    try {
      const response = await deleteRequest(quote.id, projectId!, { area, assetTypology });
      if (response.status === 'OK') {
        // Optimistic UI update to hide the deleted request while backend propagation completes.
        if (configurations?.length) {
          const updatedConfigurations = configurations
            .map((config) => ({
              ...config,
              quotes: config.quotes.filter((configQuote) => configQuote.id !== quote.id),
            }))
            .filter((config) => config.quotes.length > 0);
          dispatch(setConfigurations(updatedConfigurations));
        }

        onQuoteDeleted?.();
        // Retry refetch to handle eventual consistency on the backend.
        window.setTimeout(() => onQuoteDeleted?.(), 2000);
        window.setTimeout(() => onQuoteDeleted?.(), 5000);
      } else {
        dispatch(
          applicationStateActions.setSnackbar({
            title: t('snackbar.error.delete-quote-failed.title'),
            description: t('snackbar.error.delete-quote-failed.description'),
            open: true,
            type: 'error',
          }),
        );
      }
    } catch (error) {
      logger.error(error);
      dispatch(
        applicationStateActions.setSnackbar({
          title: t('snackbar.error.delete-quote-failed.title'),
          description: t('snackbar.error.delete-quote-failed.description'),
          open: true,
          type: 'error',
        }),
      );
    }
  };

  const getDocuments = async (documents: string[]): Promise<Document[]> => {
    const files: Document[] = [];
    for (const document of documents) {
      try {
        const res = await fetch(`${documentsBucketUrl}/${document}.pdf`);
        if (res.ok) {
          const arrayBuffer = await res.arrayBuffer();
          const base64 = btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));

          files.push({
            _id: `${document}-${Date.now()}`,
            base64: base64,
            name: `${document}.pdf`,
            type: 'application/pdf',
            url: `${documentsBucketUrl}/${document}.pdf`,
          });
        }
      } catch (error) {
        logger.error(`Failed to fetch document ${document}:`, error);
      }
    }
    return files;
  };

  const downloadDocuments = async () => {
    try {
      const res = await getClientDocuments({ clientId, projectId: projectId!, quoteId: quote.id });
      const preContractDocuments = await getDocuments(['contract-scheme', 'general', 'conditions']);

      if (!preContractDocuments.length && !res?.[0]?.documents?.length) {
        dispatch(
          applicationStateActions.setSnackbar({
            title: t('snackbar.error.no-documents-found.title'),
            description: t('snackbar.error.no-documents-found.description'),
            open: true,
            type: 'error',
          }),
        );
      } else {
        downloadZip([...(res?.[0]?.documents ?? []), ...preContractDocuments], t('documents.folderName', { quoteName: quote.name }));
      }
    } catch (error) {
      logger.error(error);
      dispatch(
        applicationStateActions.setSnackbar({
          title: t('snackbar.error.documents-download-failed.title'),
          description: t('snackbar.error.documents-download-failed.description'),
          open: true,
          type: 'error',
        }),
      );
    }
  };

  const getTaxes = () => {
    if (quote?.priceDetails?.length) {
      const taxes = quote.priceDetails.find((priceDetail) => priceDetail.key === 'vat' && priceDetail.type === 'tax');
      if (!taxes?.show) return [];
      const tax = {
        label: t('quote.taxes'),
        value: taxes?.excluded ? true : taxes?.amount,
        frequency: 'monthly',
      };
      const monthlyAmount = {
        label: t('quote.monthly-subtotal'),
        value: quote.price - taxes?.amount,
        frequency: 'monthly',
      };

      return [tax, monthlyAmount];
    }
    return [];
  };

  const getRentPayments = () => {
    if (quote?.rentPayments) {
      const monthlyExpenses = {
        label: t('quote.rentPayments.monthly-expenses'),
        subtitle: t('quote.rentPayments.monthly-expenses-subtitle'),
        value: Math.round(quote.rentPayments.expenses) ?? 0,
        frequency: 'monthly',
      };

      const securityDeposit = {
        label: t('quote.rentPayments.security-deposit'),
        subtitle: t('quote.rentPayments.security-deposit-subtitle', { months: quote.rentPayments.depositMonth }),
        value: Math.round(quote.rentPayments.depositNoExpenseNoIva) ?? 0,
        frequency: 'single',
        hideNote: true,
      };

      const advance = {
        label: t('quote.rentPayments.advance'),
        subtitle: t('quote.rentPayments.advance-subtitle'),
        value: Math.round(quote.rentPayments.advance) ?? 0,
        frequency: 'single',
        hideNote: true,
      };

      const agencyFees = {
        label: t('quote.rentPayments.agency-fees'),
        subtitle: t('quote.rentPayments.agency-fees-subtitle'),
        value: Math.round(quote.rentPayments.agencyFee) ?? 0,
        frequency: 'single',
        hideNote: true,
      };

      const monthlyRate = {
        label: t('quote.rentPayments.monthly-rate'),
        value: Math.round(quote.rentPayments.rate) ?? 0,
        frequency: 'monthly',
      };

      return [monthlyExpenses, securityDeposit, advance, agencyFees, monthlyRate];
    }
    return [];
  };

  const handleFooterActionOnClose = () => {
    if (footerActionOnClose) footerActionOnClose();
  };
  const handleFooterActionOnSubmit = () => {
    if (footerActionOnSubmit) footerActionOnSubmit();
  };

  return (
    <div className='quote-details-container'>
      <div className='quote-details-header'>
        <div className='header-info'>
          <span className='quote-name'>{quote.name}</span>
          <span className='quote-date'>{t('quote.created-on', { date: formatDate(new Date(quote.createdOn), true, false) })}</span>
        </div>
        <QuoteDetailsActions
          downloadDocuments={downloadDocuments}
          handleOpenPdf={handleOpenPdf}
          handleDeleteQuote={handleDeleteQuote}
          quoteName={quote.name}
          quoteStatus={quote.status}
        />
      </div>
      <div className={`quote-details-content${footerActionOnClose || footerActionOnSubmit ? '-with-footer' : ''}`}>
        {quote.status && quote.status !== 'init' && (
          <>
            <div className='quote-status'>
              <span className='label'>{t('quote.details.status')}</span>
              <ApartmentStatusTag label={quote.status} color={getStatusColor(quote.status)} />
            </div>
            <Divider />
          </>
        )}
        {(!!getMoodAndOptionals()?.length || !!getExtraSpaces?.length) && (
          <div className='quote-spec-sections'>
            {getMoodAndOptionals()?.length > 0 && (
              <QuoteSpecSection title={t('quote.details.sections.mood-and-optionals')} specs={getMoodAndOptionals()} />
            )}
            {/* <QuoteSpecSection title={t('quote.details.sections.promo-and-interior')} specs={PROMO_INTERIOR_PACKS} /> */}
            {!!getExtraSpaces?.length && (
              <QuoteSpecSection title={t('quote.details.sections.extra-spaces')} type='card' extraSpaces={getExtraSpaces} />
            )}
            {/* <QuoteSpecSection title={t('quote.details.sections.payment-details')} specs={PAYMENT_DETAILS} /> */}
            {assetTypology === 'room' && <Divider />}
          </div>
        )}
        {assetTypology === 'room' || area === 'sale' || getTaxes()?.length > 0 ? (
          <div className='quote-price-section'>
            {getTaxes()?.length > 0 && <QuoteSpecSection title={t('quote.details.sections.payment-details')} specs={getTaxes()} />}
            <div className='quote-price-container'>
              <div className='quote-price-label'>
                <span>{area === 'rent' ? t('quote.monthly-rate') : t('quote.total')}</span>
                <span className='quote-price-subtitle'>{t('quote.includes-taxes-and-fees')}</span>
              </div>
              <h2 className='quote-price'>{displayPrice(area === 'rent', quote.price ?? quote.rentPayments?.total) ?? 0}</h2>
            </div>
          </div>
        ) : (
          <div className='quote-price-sections'>
            {getRentPayments()?.length > 0 && <QuoteSpecSection title={t('quote.details.sections.payment-details')} specs={getRentPayments()} />}
          </div>
        )}
        {/* {ADDITIONAL_EXPENSES && (
          <>
            <Divider />
            <QuoteSpecSection title={t('quote.details.sections.additional-expenses')} specs={ADDITIONAL_EXPENSES} />
          </>
        )} */}
        <Button className='toggle-notes-button' size='small' onClick={handleOpenInformativeNotes} link>
          {t('quote.details.check-notes')}
        </Button>
      </div>
      {footerActionOnClose || footerActionOnSubmit ? (
        <div className='quote-details-footer'>
          <Divider />
          <div className='quote-details-footer-actions'>
            <Button className='select-configuration-button' onClick={handleFooterActionOnClose} outlined>
              {t('send-request.button-label.close')}
            </Button>
            <Button className='select-configuration-button' onClick={handleFooterActionOnSubmit} color='primary'>
              {t('send-request.button-label.select-configuration')}
            </Button>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default React.memo(QuoteDetailsContainer);
